import React from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import { Box } from '@stashinvest/ui';

import { linkBankSelect, linkBankMicros } from 'stash/actions/linkBank';
import { openDialog } from 'stash/actions/dialog';
import { setDebitStartFund } from 'stash/actions/signUp';
import { trackEvent, trackInteraction } from 'stash/actions/tracking';
import {
	postBuyWithDeposit,
	postAccountAutoStash,
	getExternalAccount,
} from 'stash/actions/api';
import { selectPlatformTier } from 'stash/selectors';
import { selectAccounts } from 'stash/selectors';
import { SignUpStep } from 'stash/components/sign-up/SignUpScreenLayout';
import LinkBankTypeSelect from 'stash/components/bank-link/LinkBankTypeSelect';
import LinkPlaid from 'stash/components/bank-link/plaid/LinkPlaid';
import LinkBankMicrosContainer from 'stash/components/bank-link/LinkBankMicrosContainer';

const views = {
	LINK_SELECT: 'LINK_SELECT',
	PLAID: 'PLAID',
	MICROS: 'MICROS',
};

const BANKLINK_COMPLETE_TIER_MAP = {
	BASIC_THREE: 'BankLinkCompleteTier3',
	PREMIUM_NINE: 'BankLinkCompleteTier9',
};

const mapStateToProps = (state, ownProps) => ({
	retireAccountId:
		state.entities.account_by_type.ROTH_IRA ||
		state.entities.account_by_type.TRADITIONAL_IRA,
	investAccountId: state.entities.account_by_type.PERSONAL_BROKERAGE,
	accounts: selectAccounts(state),
	currentBankAccount: state.entities.externalAccount,
	signUpData: state.application.signUp.data,
	isRetireAddOn: ownProps.product === 'retire-add-on',
	available_accounts: state.entities.available_accounts,
	initialDepositAmount: state.application.signUp.data.initial_deposit,
	platform_tier: selectPlatformTier(state),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	linkBankSelectEvent: (bank, method) => {
		return dispatch(linkBankSelect(bank, method));
	},
	getExternalAccountDetails: () => {
		return dispatch(getExternalAccount());
	},
	linkBankMicrosEvent: () => {
		return dispatch(linkBankMicros());
	},
	makeInitialDeposit: (accountId, cardId, amount) => {
		return dispatch(postBuyWithDeposit(accountId, cardId, amount));
	},
	makeRetireAddOnDepositandAS: (retireAccountId, cardId, auto_stash) => {
		dispatch(postAccountAutoStash(retireAccountId, { auto_stash }));
		dispatch(postBuyWithDeposit(retireAccountId, cardId, auto_stash.amount));
	},
	reset: () => {
		dispatch(setDebitStartFund(false));
	},
	openErrorDialog: (prompt) => {
		return dispatch(openDialog({ prompt, confirmText: 'Close' })).catch(
			() => {}
		);
	},
	trackInteraction: (name, properties = {}) => {
		return dispatch(trackInteraction(name, properties));
	},
	trackEvent: (name, properties = {}) => {
		return dispatch(trackEvent(name, properties));
	},
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
	return {
		...stateProps,
		...dispatchProps,
		...ownProps,
		completeLinkBank: () => {
			const { platform_tier } = stateProps;

			if (platform_tier && platform_tier.type) {
				const eventName = BANKLINK_COMPLETE_TIER_MAP[platform_tier.type];
				dispatchProps.trackEvent(eventName);
			}

			return ownProps.completeStep();
		},
	};
};

class SignUpLinkBank extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			view: views.LINK_SELECT,
			accountId: props.investAccountId || Object.keys(props.accounts)[0],
		};

		this.setView = this.setView.bind(this);
		this.makeDeposit = this.makeDeposit.bind(this);
	}

	setView(view) {
		this.setState({
			view: view,
		});
	}

	makeDeposit(initialDepositAmount, autostash, isRetireAddOn) {
		const { makeInitialDeposit, makeRetireAddOnDepositandAS, retireAccountId } =
			this.props;

		if (initialDepositAmount) {
			makeInitialDeposit(retireAccountId, -1, initialDepositAmount);
		}
		if (isRetireAddOn && autostash) {
			makeRetireAddOnDepositandAS(retireAccountId, -1, autostash);
		}
	}

	render() {
		const {
			completeLinkBank,
			signUpData,
			isRetireAddOn,
			initialDepositAmount,
			getExternalAccountDetails
		} = this.props;

		const autostash = signUpData && signUpData.auto_stash;

		const completionStep = () => {
			getExternalAccountDetails().then(() => {
				this.makeDeposit(
					initialDepositAmount,
					autostash,
					isRetireAddOn
				);
				completeLinkBank();
			})
		}

		const renderView = () => {
			switch (this.state.view) {
				case views.MICROS:
					return (
						<LinkBankMicrosContainer
							useBankLink={() => this.setView(views.PLAID)}
							complete={completionStep}
						/>
					);
				case views.PLAID:
					return (
						<LinkPlaid
							onSuccess={completionStep}
							onExit={() => this.setView(views.LINK_SELECT)}
							onMicrosSelect={() => this.setView(views.MICROS)}
						/>
					);
				case views.LINK_SELECT:
				default:
					return (
						<LinkBankTypeSelect
							onBankSelect={() => this.setView(views.PLAID)}
							onMicrosSelect={() => this.setView(views.MICROS)}
						/>
					);
			}
		};

		return (
			<SignUpStep className="e2e-signUp-ssn">
				<Box maxWidth="28rem" width="100%" mt="6rem">
					{renderView()}
				</Box>
			</SignUpStep>
		);
	}
}

SignUpLinkBank.propTypes = {
	investAccountId: propTypes.string,
	accounts: propTypes.object,
	linkBankSelectEvent: propTypes.func,
	available_accounts: propTypes.array,
	linkBankMicrosEvent: propTypes.func,
	trackInteraction: propTypes.func,
	retireAccountId: propTypes.string,
	initialDepositAmount: propTypes.number,
	makeInitialDeposit: propTypes.func,
	completeLinkBank: propTypes.func,
	reset: propTypes.func,
	screen: propTypes.object,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(SignUpLinkBank);
